import React, { useContext, useState } from 'react';
import { Button, Icon, Text as ShrikeText } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { isActive } from 'domain/product-statuses';
import { StandardRow } from 'components/standard-row';
import { ReactivatePlan } from 'components/modals/reactivate-plan/modal.component';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { REACTIVATION_SOURCE } from 'components/modals/reactivate-plan/modal.constants';
import { useAccountContext } from 'contexts/account';
import {
  ButtonWrapper,
  HeaderRow,
  PlanContainer,
  Details,
  Content,
} from './active-plan.style';
import { MembershipContext } from '../../membership.page';

const getCopy = copyFor('settings.membership');
interface Props {
  plan: SearchPlansForPlanDetailsQuery['search']['plans']['plans'][number];
}
export const ActivePlan = ({ plan }: Props) => {
  const { utilityAccounts, displayName, name, productStatuses } = plan;
  const utilityAccount = plan.utilityAccounts[0];
  const utilityDisplayName = utilityAccount?.utility.displayName;
  const address = utilityAccount?.displayServiceAddress;
  const utilityAccountNumber = utilityAccount?.accountNumber || '';
  const { customerType } = useAccountContext();
  const isSMB = customerType === 'smb';
  const displayServiceAddress = utilityAccounts[0]?.displayServiceAddress;
  const billPayStatus = productStatuses.billPay.status;
  const autoPayCopy = isActive(billPayStatus)
    ? getCopy('content.autoPay.active')
    : getCopy('content.autoPay.inactive');

  const [detailsSectionOpen, setDetailsSectionOpen] = useState(false);
  const [reactivatePlanModalIsOpen, setReactivatePlanModalIsOpen] =
    useState(false);
  const context = useContext(MembershipContext);
  const flag = useFlags();

  const toggleReactivationModal = event => {
    if (event?.refreshDashboard) {
      context?.setRefreshPlans(!context.refreshPlans);
    }
    setReactivatePlanModalIsOpen(!reactivatePlanModalIsOpen);
  };

  const toggleExpansion = () => {
    setDetailsSectionOpen(!detailsSectionOpen);
  };

  return (
    <PlanContainer>
      <HeaderRow onClick={toggleExpansion}>
        <Content>
          <ShrikeText textStyle="heading600" tag="h4">
            {displayName}
          </ShrikeText>
          {utilityDisplayName && isSMB && (
            <ShrikeText
              data-testid="subtitle"
              textStyle="paragraph400"
              color="secondary"
              margin={{ top: '8px' }}
            >
              {getCopy('activePlans.smbSubtitle', {
                utilityDisplayName,
                utilityAccountNumber:
                  utilityAccountNumber && `#${utilityAccountNumber}`,
                address: address && ` • ${address}`,
              })}
            </ShrikeText>
          )}
        </Content>
        <Icon
          data-testid="expand-plan-details"
          margin={{ right: '8px' }}
          icon={detailsSectionOpen ? 'ChevronUp' : 'ChevronDown'}
        />
      </HeaderRow>
      {detailsSectionOpen && (
        <Details>
          <StandardRow
            heading={getCopy('content.homeAddress')}
            text={
              displayServiceAddress || getCopy('content.noAddressAvailable')
            }
          />
          <StandardRow
            heading={getCopy('content.accountLabel')}
            text={getCopy('content.accountNumber', {
              // TODO: switch this back to plan.id as part of https://arcadiapower.atlassian.net/browse/DASH-2427
              accountNumber: plan.accountNumber,
            })}
          />
          <StandardRow
            heading={getCopy('content.autoPay.title')}
            text={autoPayCopy}
          />
          <StandardRow
            heading={getCopy('content.nickname')}
            text={name || undefined}
            iconLinkProps={{
              margin: '0px',
              to: routes({ planId: plan.id }).planNickname,
            }}
            hasBottomBorder={true}
          />
          {flag.lbReactivationPlanEnabled &&
            !plan.isExclusiveMembershipPlan &&
            plan.reactivationEligibility.eligible &&
            !plan.active && (
              <ButtonWrapper>
                <Button
                  color="primary"
                  backgroundColor="primaryInverse"
                  size="small"
                  onClick={toggleReactivationModal}
                  data-testid="reactivate-plan-button"
                >
                  <Icon
                    color="primaryInverse"
                    icon="LightningBolt"
                    margin={{ left: '5px', right: '10px' }}
                  />
                  {getCopy('reactivateButton')}
                </Button>
              </ButtonWrapper>
            )}
        </Details>
      )}
      {reactivatePlanModalIsOpen && (
        <ReactivatePlan
          planId={plan.id}
          planNickname={plan.displayName}
          onClose={toggleReactivationModal}
          source={REACTIVATION_SOURCE.SETTINGS}
        />
      )}
    </PlanContainer>
  );
};
